import {
  deleteBlobMutation,
  getBaseUrl,
  listBlobsQuery,
  setBlobMutation,
  UserFriendlyError,
} from '@pique/graphql';
import type { BlobStorage } from '@toeverything/infra';
import { BlobStorageOverCapacity } from '@toeverything/infra';

import type { GraphQLService } from '../../../cloud';
import { getPiqueCloudBaseUrl } from '../../../cloud/services/fetch';
import { addTauriAuthHeaders } from '../../../cloud/utils/tauri-auth';
import { bufferToBlob } from '../../utils/buffer-to-blob';

export class CloudBlobStorage implements BlobStorage {
  constructor(
    private readonly workspaceId: string,
    private readonly graphqlService: GraphQLService
  ) { }

  name = 'cloud';
  readonly = false;

  async get(key: string) {
    const suffix = key.startsWith('/')
      ? key
      : `/api/workspaces/${this.workspaceId}/blobs/${key}`;

    // Ensure Tauri auth header and correct base URL (works for both web and Tauri)
    const baseUrl = environment.isTauri ? getPiqueCloudBaseUrl() : getBaseUrl();
    const headers = await addTauriAuthHeaders({});

    const res = await fetch(baseUrl + suffix, {
      cache: 'default',
      headers,
    });
    if (!res.ok) {
      return null;
    }
    return bufferToBlob(await res.arrayBuffer());
  }

  async set(key: string, value: Blob) {
    // set blob will check blob size & quota
    return await this.graphqlService
      .gql({
        query: setBlobMutation,
        variables: {
          workspaceId: this.workspaceId,
          blob: new File([value], key),
        },
      })
      .then(res => res.setBlob)
      .catch(err => {
        const error = UserFriendlyError.fromAnyError(err);
        if (error.status === 413) {
          throw new BlobStorageOverCapacity(error);
        }

        throw err;
      });
  }

  async delete(key: string) {
    await this.graphqlService.gql({
      query: deleteBlobMutation,
      variables: {
        workspaceId: key,
        hash: key,
      },
    });
  }

  async list() {
    const result = await this.graphqlService.gql({
      query: listBlobsQuery,
      variables: {
        workspaceId: this.workspaceId,
      },
    });
    return result.listBlobs;
  }
}
